import { useState, useEffect, useMemo, useCallback, useRef } from "react";

import { useTranslate } from "_contexts";
import { isEmptyArray, isNonEmptyArray } from "_helpers";
import { getReportingExtraGrid, getReporting, getReportingDownload, getReportingDownloadPdf } from "_services";

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

export function useReportingGridGroups( initialColumDefs ){
    const [ columnDefs, setColumnDefs] = useState([]);
    const [ fields, setFields ] = useState([]);
    const [ doRedirect, setDoRedirect ] = useState(false);

    useEffect(() => {
        if( isNonEmptyArray(initialColumDefs) ){
            try {
                const timer = setTimeout(async () => {
                    const { response, data, redirect } = await getReportingExtraGrid();

                    if( response ){
                        const fetchGrid = data.config;
                        const totalObj = [{
                            headerName: "", groupId: "end", children: [
                                { headerName: "Solde", field: "amount_reste", type: "valueColumn" },
                            ]
                        }];
                        
                        setColumnDefs([...initialColumDefs, ...fetchGrid, ...totalObj]);
                        
                        const fetchFields = data.fields;
                        setFields(fetchFields);
                    } else {
                        setColumnDefs([]);
                        setFields([]);
                    }

                    setDoRedirect(redirect);
                }, 0)
    
                return () => clearTimeout(timer);
            } catch(e){
                setColumnDefs([]);
                setFields([]);
                setDoRedirect(false);
                throw new Error(e);
            }
        }
    }, [initialColumDefs])

    return {
        fetchColumnDefs: columnDefs,
        fetchTableFields: fields,
        reportingGridGroupsDoRedirect: doRedirect
    };
}

export function useReporting( form ){
    const [ reportingData, setReportingData ] = useState([]);
    const [ reportingToBeChecked, setReportingToBeChecked ] = useState([]);
    const [ billingData, setBillingData ] = useState({});

    const [ exportId, setExportId ] = useState();
    
    const [ doRedirect, setDoRedirect ] = useState(false);
    const [ formIsSubmitted, setFormIsSubmitted ] = useState(false);

    const formatEN = useMemo(() => "YYYY-MM-DD", []);

    const resetValues = useCallback(() => {
        setReportingData([]);
        setReportingToBeChecked([]);
        setBillingData({});
        setExportId(null);
    }, []);

    const getReportingValues = useCallback(() => {
        try {
            const timer = setTimeout(async () => {
                let values = form.getFieldsValue();
                values['scopeDateStart'] = values['constructDates']?.[0] ? dayjs(values['constructDates'][0]).format(formatEN) : null;
                values['scopeDateEnd'] = values['constructDates']?.[1] ? dayjs(values['constructDates'][1]).format(formatEN) : null;
                values['calcDateStart'] = values['displayDates']?.[0] ? dayjs(values['displayDates'][0]).format(formatEN) : null;
                values['calcDateEnd'] = values['displayDates']?.[1] ?  dayjs(values['displayDates'][1]).format(formatEN) : null;
                delete(values['constructDates']);
                delete(values['displayDates']);
                
                const errors = form.getFieldsError();
                const isInvalid = isNonEmptyArray(errors.filter(el => isNonEmptyArray(el.errors)));

                if( isInvalid ) return ; // Sécurité supplémentaire
            
                const { response, data, exportId, redirect } = await getReporting(values);

                if( response ){
                    const reporting = data.reporting;
                    const billing = data.billing;

                    setReportingData(reporting.records);
                    setReportingToBeChecked(reporting.to_be_checked);
                    setBillingData(billing.records);
                    setExportId(exportId)
                } else {
                    resetValues();
                }

                setDoRedirect(redirect);
                setFormIsSubmitted(false);
            }, 0)

            return () => clearTimeout(timer);
        } catch(e){
            resetValues();
            setFormIsSubmitted(false);
            throw new Error(e);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[form])

    useEffect(() => {
        if( formIsSubmitted === true ){
            resetValues();
            getReportingValues();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formIsSubmitted, resetValues, getReportingValues]);

    return {
        fetchReportingData: reportingData,
        fetchReportingToBeChecked: reportingToBeChecked,
        fetchBillingData: billingData,
        reportingDoRedirect: doRedirect,
        exportId,
        formIsSubmitted,
        resetValues,
        setFormIsSubmitted
    }
}

export function useReportingDownload( reportingData ){
    const { lang } = useTranslate();

    const [ doRedirect, setDoRedirect ] = useState(false);
    const [ downloadIsPending, setDownloadIsPending ] = useState(false);

    const initiateDownload = useCallback(async ( bases, customer, endDate ) => {
        if( isEmptyArray(reportingData) ) return;

        setDownloadIsPending(true);
        
        try {
            // Définir un timer pour imiter la latence ou ajuster si nécessaire
            const { response, redirect } = await getReportingDownload({
                reporting_data: reportingData,
                bases,
                customer,
                end_date: endDate,
                lang
            });

            if (!response) {
                // Alert message ?
            }

            setDoRedirect(redirect);
        } catch(e) {
            setDoRedirect(false);
            console.error("Erreur dans initiateDownload:", e);
            // throw new Error(e);
        } finally {
            setDownloadIsPending(false);
        }
    }, [reportingData, lang]);

    return {
        reportingDownloadDoRedirect: doRedirect,
        downloadIsPending,
        initiateDownload
    };
}

export function useReportingDownloadPdf( reportingData, detailledReportValue ){
    const formatEN = useRef("YYYY-MM-DD").current;

    const [ doRedirect, setDoRedirect ] = useState(false);
    const [ downloadPdfIsPending, setDownloadPdfIsPending ] = useState(false);

    const initiateDownloadPdf = useCallback(async ( bases, customer, endDate ) => {
        if( isEmptyArray(reportingData) ) return;

        if( detailledReportValue ) endDate =  dayjs().format(formatEN);

        setDownloadPdfIsPending(true);
        
        try {
            // Définir un timer pour imiter la latence ou ajuster si nécessaire
            const { response, redirect } = await getReportingDownloadPdf({
                reporting_data: reportingData,
                bases,
                customer,
                end_date: endDate
            });

            if (!response) {
                // Alert message ?
            }

            setDoRedirect(redirect);
        } catch(e) {
            setDoRedirect(false);
            console.error("Erreur dans initiateDownloadPdf:", e);
            // throw new Error(e);
        } finally {
            setDownloadPdfIsPending(false);
        }
    }, [reportingData, detailledReportValue, formatEN]);

    return {
        reportingDownloadPdfDoRedirect: doRedirect,
        downloadPdfIsPending,
        initiateDownloadPdf
    };
}